import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Printer,
  Check,
  RotateCcw,
  Euro,
  Scissors,
  CheckCircle2,
  PackageCheck,
  Wallet,
  BadgeEuro,
  Share2,
  Link as LinkIcon,
  Calendar,
  FileText,
  Download,
  Camera,
  ImagePlus,
  X,
  Eye,
  EyeOff,
  Pencil,
} from "lucide-react";
import { toast } from "sonner";
import {
  getQuote,
  listScreenTypes,
  listColorOptions,
  toggleCutCheck,
  resetCutChecks,
  confirmOrder,
  unconfirmOrder,
  markPayment,
  clearPayment,
  getAppSettings,
  getWorkshopLink,
  setQuoteSchedule,
  updateRoom,
  issueInvoice,
  getInvoicePdfBlob,
  getCutSheetPdfBlob,
  updateQuoteMeta,
  listCustomers,
  addQuotePhoto,
  setQuoteCustomerNumber,
  getNextCustomerNumber,
  removeQuotePhoto,
} from "@/lib/api";
import SketchPad from "@/components/SketchPad";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

// Tiny inline numeric input that commits on blur, Enter, or the Update button.
// Robust on iPad: uses pointer events + click fallback, and a "saving" state
// so workshop staff get clear feedback that their value was applied.
const LeafInput = ({ value, onCommit, testId }) => {
  const [draft, setDraft] = useState(value ?? "");
  const [saving, setSaving] = useState(false);
  const inFlightRef = useRef(false);
  useEffect(() => {
    setDraft(value ?? "");
  }, [value]);
  const commit = async () => {
    const a = (draft ?? "").toString().trim();
    const b = (value ?? "").toString().trim();
    if (a === b) return;
    if (inFlightRef.current) return; // guard against double-fire (pointerdown + click)
    inFlightRef.current = true;
    setSaving(true);
    try {
      await Promise.resolve(onCommit(a));
    } finally {
      inFlightRef.current = false;
      setSaving(false);
    }
  };
  const isDirty = (draft ?? "").toString().trim() !== (value ?? "").toString().trim();
  return (
    <span className="inline-flex items-center gap-1">
      <input
        type="number"
        min="0"
        step="1"
        inputMode="numeric"
        placeholder="—"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") setDraft(value ?? "");
        }}
        data-testid={testId}
        className="w-20 border border-zinc-300 px-2 py-1 font-mono text-xs tabular-nums bg-white rounded-none focus:outline-none focus:border-[#09090b]"
      />
      {(isDirty || saving) && (
        <button
          type="button"
          // Pointer-down keeps the input focused (preventDefault) so blur
          // doesn't race; click is a fallback for iPad taps that suppress
          // pointerdown. The inFlightRef inside commit() guards both paths.
          onPointerDown={(e) => {
            e.preventDefault();
            commit();
          }}
          onClick={() => {
            commit();
          }}
          disabled={saving}
          className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] font-semibold bg-[#09090b] text-white hover:bg-[#005bea] transition-colors disabled:opacity-60"
          data-testid={`${testId}-update`}
          aria-label="Update value"
        >
          {saving ? "Saving…" : "Update"}
        </button>
      )}
    </span>
  );
};

// Inline-editable room name. Click to edit, Enter to save, Esc to cancel.
const RoomNameEdit = ({ value, onCommit, className = "", testId }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  useEffect(() => {
    setDraft(value ?? "");
  }, [value]);
  const commit = () => {
    const next = (draft ?? "").toString().trim();
    setEditing(false);
    if (!next || next === (value ?? "").toString().trim()) return;
    onCommit(next);
  };
  if (editing) {
    return (
      <input
        autoFocus
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") {
            setDraft(value ?? "");
            setEditing(false);
          }
        }}
        data-testid={testId}
        // Force black text on white background while editing — the parent's
        // text-white (e.g. on the dark quote header) would otherwise make
        // the input invisible.
        className={`bg-white text-[#09090b] border border-[#09090b] px-2 py-0.5 outline-none ${className}`}
      />
    );
  }
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      data-testid={testId}
      title="Click to rename"
      className={`text-left hover:bg-yellow-100 hover:text-[#09090b] px-1 -mx-1 rounded-sm transition-colors ${className}`}
    >
      {value || "(unnamed)"}
    </button>
  );
};

const QuoteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [quote, setQuote] = useState(null);
  const [types, setTypes] = useState([]);
  const [colors, setColors] = useState([]);
  const [checks, setChecks] = useState(new Set());
  const [view, setView] = useState(
    // Honour deep-links from the Day Plan ("/quotes/{id}#cut") so opening a
    // job from the workshop schedule lands directly on the Cut Sheet.
    location.hash === "#cut" ? "cut" : "price"
  ); // "price" | "cut"
  const [confirming, setConfirming] = useState(false);
  const [workshop, setWorkshop] = useState({ workshop_name: "", workshop_phone: "" });
  const [workshopUrl, setWorkshopUrl] = useState("");
  const [savingPdf, setSavingPdf] = useState(false);
  const [customerNumberLabel, setCustomerNumberLabel] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  // `${roomId}:${label}` of the cut-piece tile currently being overridden.
  // Empty string = no tile open for editing.
  const [editingPieceKey, setEditingPieceKey] = useState("");
  const [editingPieceDraft, setEditingPieceDraft] = useState("");
  const longPressTimerRef = useRef(null);
  // Room id of the room whose sketchpad is currently open in the cut sheet.
  // Empty string = no sketchpad open. Only one open at a time to keep the
  // page tidy (long sketches push other rooms down).
  const [sketchOpenRoomId, setSketchOpenRoomId] = useState("");
  // MN-XXX inline editor on the quote header. `mnEditorOpen` toggles the
  // little popover; `mnDraft` is the typed integer (string for input flex).
  const [mnEditorOpen, setMnEditorOpen] = useState(false);
  const [mnDraft, setMnDraft] = useState("");
  const [mnSuggestion, setMnSuggestion] = useState("");

  useEffect(() => {
    Promise.all([getQuote(id), listScreenTypes(), listColorOptions(), getAppSettings()])
      .then(([q, t, c, app]) => {
        setQuote(q);
        setTypes(t);
        setColors(c || []);
        setChecks(new Set(q.cut_checks || []));
        setWorkshop(app || { workshop_name: "", workshop_phone: "" });
        // Resolve the MN-XXX label for this quote — per-quote override wins
        // over the customer's directory entry. This keeps two customers
        // sharing a first name (e.g. two Kellys) on different MN numbers
        // without having to rename either of them.
        const overrideNum = q.customer_number_override;
        if (Number.isFinite(overrideNum) && overrideNum > 0) {
          setCustomerNumberLabel(`MN-${String(overrideNum).padStart(3, "0")}`);
        } else if (q.client_name) {
          listCustomers()
            .then((all) => {
              const match = all.find(
                (c) => (c.name || "").toLowerCase() === q.client_name.toLowerCase()
              );
              setCustomerNumberLabel(match?.customer_number_label || "");
            })
            .catch(() => {});
        }
      })
      .catch(() => {
        toast.error("Quote not found");
        navigate("/quotes");
      });
  }, [id, navigate]);

  // React to hash changes (e.g. when navigating from /schedule with `#cut`)
  // so the Cut Sheet view opens immediately, not the price view.
  useEffect(() => {
    if (location.hash === "#cut") setView("cut");
  }, [location.hash, id]);

  // Build the workshop share URL whenever settings (specifically `public_url`)
  // are loaded or change. Done in a separate effect so we re-render the URL
  // with the canonical domain once settings arrive — without loops.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    getWorkshopLink(id)
      .then((d) => {
        if (cancelled) return;
        const origin =
          (workshop && workshop.public_url) || window.location.origin;
        setWorkshopUrl(`${origin}/workshop/${d.quote_id}/${d.token}`);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [id, workshop]);

  const totalPieces = useMemo(() => {
    if (!quote) return 0;
    return quote.rooms.reduce((sum, r) => sum + (r.cut_pieces?.length || 0), 0);
  }, [quote]);

  const toggleCheck = async (key) => {
    const willBeDone = !checks.has(key);
    // optimistic update
    const next = new Set(checks);
    if (willBeDone) next.add(key);
    else next.delete(key);
    setChecks(next);
    try {
      const resp = await toggleCutCheck(id, key, willBeDone);
      // Update produced_at locally from server response
      setQuote((q) => (q ? { ...q, produced_at: resp.produced_at || null } : q));
    } catch {
      // revert
      setChecks(checks);
      toast.error("Failed to update");
    }
  };

  const resetAll = async () => {
    if (!window.confirm("Clear all cut check-marks?")) return;
    try {
      await resetCutChecks(id);
      setChecks(new Set());
      setQuote((q) => (q ? { ...q, produced_at: null } : q));
      toast.success("Cut list reset");
    } catch {
      toast.error("Reset failed");
    }
  };

  // ----- Photo upload (for manual quotes that need a sketch added later) -----
  const fileToDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error || new Error("read failed"));
      reader.readAsDataURL(file);
    });

  // Downscale large camera photos so the data URL stays under ~3 MB.
  const downscaleImage = (file, maxDim = 2200) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const ratio = Math.min(1, maxDim / Math.max(img.width, img.height));
          if (ratio === 1) {
            resolve(reader.result);
            return;
          }
          const canvas = document.createElement("canvas");
          canvas.width = Math.round(img.width * ratio);
          canvas.height = Math.round(img.height * ratio);
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL("image/jpeg", 0.85));
        };
        img.onerror = () => resolve(reader.result); // fall back to raw
        img.src = reader.result;
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    });

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-selecting the same file
    if (files.length === 0) return;
    setUploadingPhoto(true);
    try {
      let updated = null;
      for (const file of files) {
        if (!file.type.startsWith("image/")) {
          toast.error(`${file.name}: not an image`);
          continue;
        }
        const dataUrl =
          file.size > 1.2 * 1024 * 1024
            ? await downscaleImage(file)
            : await fileToDataUrl(file);
        if (!dataUrl) continue;
        updated = await addQuotePhoto(id, dataUrl);
      }
      if (updated) {
        setQuote(updated);
        toast.success(files.length > 1 ? "Photos attached" : "Photo attached");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Upload failed";
      toast.error(msg);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = async (index) => {
    if (!window.confirm("Remove this photo from the quote?")) return;
    try {
      const updated = await removeQuotePhoto(id, index);
      setQuote(updated);
      toast.success("Photo removed");
    } catch {
      toast.error("Remove failed");
    }
  };

  const handleConfirm = async () => {
    if (confirming) return;
    setConfirming(true);
    try {
      const resp = await confirmOrder(id);
      setQuote((q) => (q ? { ...q, confirmed_at: resp.confirmed_at } : q));
      toast.success("Order confirmed");
      setView("cut");
    } catch {
      toast.error("Confirm failed");
    } finally {
      setConfirming(false);
    }
  };

  const handleUnconfirm = async () => {
    if (!window.confirm("Undo confirmation? The quote will return to draft.")) return;
    try {
      await unconfirmOrder(id);
      setQuote((q) => (q ? { ...q, confirmed_at: null, scheduled_cut_date: null } : q));
      toast.success("Reverted to draft");
    } catch {
      toast.error("Failed");
    }
  };

  const handleSchedule = async () => {
    const current = quote?.scheduled_cut_date || "";
    const input = window.prompt(
      "Schedule cut date (YYYY-MM-DD). Leave blank to unschedule.",
      current
    );
    if (input === null) return;
    const trimmed = input.trim();
    const dateOrNull = trimmed === "" ? null : trimmed;
    if (dateOrNull && !/^\d{4}-\d{2}-\d{2}$/.test(dateOrNull)) {
      toast.error("Use format YYYY-MM-DD");
      return;
    }
    try {
      const resp = await setQuoteSchedule(id, dateOrNull);
      setQuote((q) => (q ? { ...q, scheduled_cut_date: resp.scheduled_cut_date } : q));
      toast.success(
        resp.scheduled_cut_date
          ? `Scheduled for ${resp.scheduled_cut_date}`
          : "Unscheduled"
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't schedule");
    }
  };

  const handleInvoice = async () => {
    if (!quote) return;
    try {
      // Allocate invoice number if needed
      let invNumber = quote.invoice_number;
      if (!invNumber) {
        const resp = await issueInvoice(id);
        invNumber = resp.invoice_number;
        setQuote((q) =>
          q
            ? { ...q, invoice_number: invNumber, invoiced_at: resp.invoiced_at }
            : q
        );
        toast.success(`Invoice ${invNumber} issued`);
      }
      // Download the PDF
      const blob = await getInvoicePdfBlob(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${invNumber}_${(quote.client_name || "invoice").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoke later so iOS Safari has time to read the blob
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't generate invoice");
    }
  };

  const handleDeposit = async () => {
    const suggested = quote?.total ? (quote.total * 0.3).toFixed(2) : "";
    const input = window.prompt(
      `Deposit amount paid (€) — leave blank for none:`,
      suggested
    );
    if (input === null) return;
    const amount = input.trim() === "" ? null : Number(input);
    if (input.trim() !== "" && (isNaN(amount) || amount < 0)) {
      toast.error("Invalid amount");
      return;
    }
    try {
      const resp = await markPayment(id, "deposit", amount);
      setQuote((q) =>
        q
          ? {
              ...q,
              deposit_paid_at: resp.deposit_paid_at,
              deposit_amount: resp.deposit_amount,
            }
          : q
      );
      toast.success("Deposit recorded");
    } catch {
      toast.error("Failed");
    }
  };

  const handleFullPayment = async () => {
    try {
      const resp = await markPayment(id, "final", null);
      setQuote((q) => (q ? { ...q, final_paid_at: resp.final_paid_at } : q));
      toast.success("Paid in full");
    } catch {
      toast.error("Failed");
    }
  };

  const handleClearPayment = async (kind) => {
    if (!window.confirm(`Remove ${kind} payment stamp?`)) return;
    try {
      await clearPayment(id, kind);
      setQuote((q) => {
        if (!q) return q;
        if (kind === "deposit")
          return { ...q, deposit_paid_at: null, deposit_amount: null };
        return { ...q, final_paid_at: null };
      });
      toast.success("Removed");
    } catch {
      toast.error("Failed");
    }
  };

  if (!quote) {
    return (
      <div className="p-10 font-mono text-sm text-zinc-500">Loading...</div>
    );
  }

  const typeLabel = (key) => types.find((t) => t.key === key)?.label || "—";

  const handleRoomTypeChange = async (roomId, newTypeKey) => {
    try {
      const updated = await updateRoom(id, roomId, { screen_type: newTypeKey || "" });
      setQuote(updated);
      setChecks(new Set(updated.cut_checks || []));
      toast.success("Screen type updated");
    } catch {
      toast.error("Couldn't update");
    }
  };

  const handleRoomColorChange = async (roomId, newColorKey) => {
    try {
      const updated = await updateRoom(id, roomId, { color_key: newColorKey || "" });
      setQuote(updated);
      toast.success("Colour updated");
    } catch {
      toast.error("Couldn't update colour");
    }
  };

  const handleRoomColorDetailChange = async (roomId, newDetail) => {
    const room = quote?.rooms?.find((rr) => rr.id === roomId);
    if (!room) return;
    if ((room.color_detail || "") === (newDetail || "").trim()) return;
    try {
      const updated = await updateRoom(id, roomId, {
        color_detail: (newDetail || "").trim(),
      });
      setQuote(updated);
    } catch {
      toast.error("Couldn't save colour spec");
    }
  };

  const handleRoomLeafChange = async (roomId, raw) => {
    const trimmed = (raw ?? "").toString().trim();
    const value = trimmed === "" ? null : Number(trimmed);
    if (value !== null && (!Number.isFinite(value) || value < 0)) {
      toast.error("Leaf width must be a positive number");
      return;
    }
    try {
      const updated = await updateRoom(id, roomId, { leaf_width_mm: value });
      setQuote(updated);
      setChecks(new Set(updated.cut_checks || []));
    } catch {
      toast.error("Couldn't update leaf width");
    }
  };

  const handleRoomTrackChange = async (roomId, raw) => {
    const trimmed = (raw ?? "").toString().trim();
    const value = trimmed === "" ? null : Number(trimmed);
    if (value !== null && (!Number.isFinite(value) || value < 0)) {
      toast.error("Track width must be a positive number");
      return;
    }
    try {
      const updated = await updateRoom(id, roomId, { track_width_mm: value });
      setQuote(updated);
      setChecks(new Set(updated.cut_checks || []));
    } catch {
      toast.error("Couldn't update track width");
    }
  };

  // Per-room manual override for a single cut piece (keyed by label).
  // Passing `null` or empty clears the override and restores the formula.
  const handlePieceOverride = async (roomId, label, newValue) => {
    const room = (quote?.rooms || []).find((r) => r.id === roomId);
    if (!room) return;
    const currentMap = { ...(room.piece_overrides || {}) };
    const trimmed = (newValue ?? "").toString().trim();
    if (trimmed === "" || Number(trimmed) <= 0) {
      delete currentMap[label];
    } else {
      const n = Number(trimmed);
      if (!Number.isFinite(n) || n <= 0) {
        toast.error("Length must be a positive number");
        return;
      }
      currentMap[label] = n;
    }
    try {
      const updated = await updateRoom(id, roomId, { piece_overrides: currentMap });
      setQuote(updated);
      // Overrides don't reset ticks (shape unchanged) — keep local checks
      setChecks(new Set(updated.cut_checks || []));
    } catch {
      toast.error("Couldn't update length");
    }
  };

  // Per-piece shape annotation ("U", "L", or "" to clear). Same key style as
  // overrides — by piece label.
  const handlePieceShape = async (roomId, label, shape) => {
    const room = (quote?.rooms || []).find((r) => r.id === roomId);
    if (!room) return;
    const currentMap = { ...(room.piece_shapes || {}) };
    if (shape === "U" || shape === "L") {
      currentMap[label] = shape;
    } else {
      delete currentMap[label];
    }
    try {
      const updated = await updateRoom(id, roomId, { piece_shapes: currentMap });
      setQuote(updated);
      setChecks(new Set(updated.cut_checks || []));
    } catch {
      toast.error("Couldn't update shape");
    }
  };

  // Save / clear a hand-drawn sketch on a room. Pass empty string to clear.
  const handleSaveSketch = async (roomId, dataUrl) => {
    try {
      const updated = await updateRoom(id, roomId, {
        sketch_data_url: dataUrl || "",
      });
      setQuote(updated);
      setChecks(new Set(updated.cut_checks || []));
      setSketchOpenRoomId("");
      toast.success(dataUrl ? "Sketch saved" : "Sketch removed");
    } catch {
      toast.error("Couldn't save sketch");
    }
  };

  // Open the MN-XXX override editor: pre-fill with current override (or the
  // backend's "next available" suggestion if the quote currently inherits
  // its number from the directory).
  const openMnEditor = async () => {
    setMnEditorOpen(true);
    const cur = quote?.customer_number_override;
    if (Number.isFinite(cur) && cur > 0) {
      setMnDraft(String(cur));
    } else {
      setMnDraft("");
    }
    try {
      const next = await getNextCustomerNumber();
      setMnSuggestion(String(next.number));
    } catch {
      setMnSuggestion("");
    }
  };

  // Save (or clear with empty string) the MN-XXX override on the current quote.
  const handleSaveMnOverride = async (clearIt = false) => {
    const val = clearIt
      ? null
      : (() => {
          const n = parseInt(String(mnDraft).trim(), 10);
          return Number.isFinite(n) && n > 0 ? n : null;
        })();
    if (!clearIt && val === null) {
      toast.error("Enter a positive whole number (or tap Reset to clear)");
      return;
    }
    try {
      const updated = await setQuoteCustomerNumber(id, val);
      setQuote(updated);
      const overrideNum = updated?.customer_number_override;
      if (Number.isFinite(overrideNum) && overrideNum > 0) {
        setCustomerNumberLabel(`MN-${String(overrideNum).padStart(3, "0")}`);
      } else if (updated.client_name) {
        // Fall back to directory entry
        try {
          const all = await listCustomers();
          const match = all.find(
            (c) =>
              (c.name || "").toLowerCase() ===
              (updated.client_name || "").toLowerCase()
          );
          setCustomerNumberLabel(match?.customer_number_label || "");
        } catch {
          /* non-fatal */
        }
      } else {
        setCustomerNumberLabel("");
      }
      setMnEditorOpen(false);
      toast.success(clearIt ? "MN reset to customer default" : "MN updated");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't update MN number";
      toast.error(msg);
    }
  };

  const handleToggleRoomExcluded = async (roomId, currentlyExcluded) => {
    try {
      const updated = await updateRoom(id, roomId, { excluded: !currentlyExcluded });
      setQuote(updated);
      setChecks(new Set(updated.cut_checks || []));
      toast.success(currentlyExcluded ? "Restored to cut sheet" : "Hidden from cut sheet");
    } catch {
      toast.error("Couldn't update room");
    }
  };

  const handleRoomNameChange = async (roomId, newName) => {
    try {
      const updated = await updateRoom(id, roomId, { name: newName });
      setQuote(updated);
      toast.success("Renamed");
    } catch {
      toast.error("Couldn't rename");
    }
  };

  const handleClientNameChange = async (newName) => {
    const trimmed = (newName ?? "").toString().trim();
    if (!trimmed) {
      toast.error("Customer name can't be empty");
      return;
    }
    if (trimmed === (quote?.client_name || "").trim()) return;
    try {
      const updated = await updateQuoteMeta(id, { client_name: trimmed });
      setQuote(updated);
      // Refresh the MN-XXX label — per-quote override wins over directory.
      const overrideNum = updated?.customer_number_override;
      if (Number.isFinite(overrideNum) && overrideNum > 0) {
        setCustomerNumberLabel(`MN-${String(overrideNum).padStart(3, "0")}`);
      } else {
        try {
          const all = await listCustomers();
          const match = all.find(
            (c) => (c.name || "").toLowerCase() === trimmed.toLowerCase()
          );
          setCustomerNumberLabel(match?.customer_number_label || "");
        } catch {
          /* non-fatal */
        }
      }
      toast.success("Customer renamed");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Rename failed";
      toast.error(msg);
    }
  };

  const handleRoomDimChange = async (roomId, field, raw) => {
    const trimmed = (raw ?? "").toString().trim();
    if (trimmed === "") return;
    const value = Number(trimmed);
    if (!Number.isFinite(value) || value <= 0) {
      toast.error(`${field === "width_mm" ? "Width" : "Height"} must be a positive number`);
      return;
    }
    try {
      const updated = await updateRoom(id, roomId, { [field]: value });
      setQuote(updated);
      setChecks(new Set(updated.cut_checks || []));
    } catch {
      toast.error("Couldn't update");
    }
  };
  const doneCount = checks.size;

  const buildWhatsAppText = () => {
    if (!quote) return "";
    const lines = [];

    if (view === "cut") {
      // Internal / workshop format
      const header = [];
      header.push(`*${quote.client_name || "Quote"}*`);
      if (quote.area) header.push(quote.area);
      if (quote.color) header.push(`Color: ${quote.color}`);
      header.push(`Date: ${new Date(quote.created_at).toLocaleDateString()}`);
      lines.push(header.join(" · "));

      const stamps = [];
      if (quote.confirmed_at) stamps.push(`✅ CONFIRMED ${fmtDate(quote.confirmed_at)}`);
      if (quote.produced_at) stamps.push(`📦 DONE ${fmtDate(quote.produced_at)}`);
      if (quote.deposit_paid_at) {
        const amt = quote.deposit_amount != null ? ` €${quote.deposit_amount.toFixed(2)}` : "";
        stamps.push(`💰 DEPOSIT${amt} ${fmtDate(quote.deposit_paid_at)}`);
      }
      if (quote.final_paid_at) stamps.push(`💶 PAID IN FULL ${fmtDate(quote.final_paid_at)}`);
      if (stamps.length) {
        lines.push("");
        lines.push(stamps.join(" · "));
      }

      lines.push("");
      lines.push("*CUT LIST*");
      quote.rooms.forEach((r, idx) => {
        lines.push("");
        lines.push(`${String(idx + 1).padStart(2, "0")}. *${r.name}* — ${typeLabel(r.screen_type)}`);
        lines.push(`   opening ${r.width_mm} × ${r.height_mm} mm`);
        if (r.cut_pieces && r.cut_pieces.length > 0) {
          r.cut_pieces.forEach((p, pi) => {
            const checked = checks.has(`${r.id}:${pi}`) ? "✅ " : "";
            lines.push(`   ${checked}${p.qty}/ ${p.length_mm}  (${p.label})`);
          });
        }
      });
      return lines.join("\n");
    }

    // Customer-facing price quote
    if (workshop.business_name) {
      lines.push(`*${workshop.business_name}*`);
      lines.push("");
    }
    const firstName = (quote.client_name || "").split(" ")[0] || "";
    const tag = customerNumberLabel ? `[${customerNumberLabel}] ` : "";
    lines.push(
      firstName
        ? `${tag}Hi ${firstName}, here's your mosquito-screen quote:`
        : `${tag}Here's your mosquito-screen quote:`
    );
    lines.push("");
    lines.push(`*Date:* ${new Date(quote.created_at).toLocaleDateString()}`);
    if (quote.area) lines.push(`*Area:* ${quote.area}`);
    if (quote.color) lines.push(`*Colour:* ${quote.color}`);
    lines.push("");

    quote.rooms.forEach((r, idx) => {
      lines.push(
        `${String(idx + 1).padStart(2, "0")}. *${r.name}*  ${r.width_mm}×${r.height_mm} mm  —  ${typeLabel(r.screen_type)}  —  *€${r.price.toFixed(2)}*`
      );
    });
    lines.push("");
    const subtotal = quote.total || 0;
    const discountAmount = quote.discount_amount || 0;
    const vatRate = quote.cash_payment ? 0 : Number(workshop.vat_rate || 0);
    if (discountAmount > 0) {
      const baseBeforeDiscount = subtotal + discountAmount;
      const pctSuffix =
        quote.discount_mode === "amount"
          ? ""
          : quote.discount_pct
            ? ` (${quote.discount_pct}%)`
            : "";
      lines.push(`Subtotal: €${baseBeforeDiscount.toFixed(2)}`);
      lines.push(`Discount${pctSuffix}: -€${discountAmount.toFixed(2)}`);
    }
    if (vatRate > 0) {
      const vatAmount = +(subtotal * vatRate / 100).toFixed(2);
      const grand = +(subtotal + vatAmount).toFixed(2);
      if (discountAmount > 0) {
        lines.push(`After discount: €${subtotal.toFixed(2)}`);
      } else {
        lines.push(`Subtotal: €${subtotal.toFixed(2)}`);
      }
      lines.push(`IVA (${vatRate}%): €${vatAmount.toFixed(2)}`);
      lines.push(`*TOTAL: €${grand.toFixed(2)}*`);
    } else {
      lines.push(`*TOTAL: €${subtotal.toFixed(2)}*`);
    }

    if (quote.deposit_paid_at || quote.final_paid_at) {
      lines.push("");
      if (quote.deposit_paid_at) {
        const amt = quote.deposit_amount != null
          ? ` (€${quote.deposit_amount.toFixed(2)})`
          : "";
        lines.push(`✅ Deposit received${amt} · ${fmtDate(quote.deposit_paid_at)}`);
      }
      if (quote.final_paid_at) {
        lines.push(`✅ Paid in full · ${fmtDate(quote.final_paid_at)}`);
      }
    }

    lines.push("");
    lines.push("Let me know if you'd like to proceed. Thanks!");
    if (workshop.business_name || workshop.business_phone) {
      lines.push("");
      const sig = [workshop.business_name, workshop.business_phone]
        .filter(Boolean)
        .join(" · ");
      if (sig) lines.push(`— ${sig}`);
    }
    return lines.join("\n");
  };

  const shareWhatsApp = () => {
    const text = buildWhatsAppText();
    let url;
    if (view === "cut" && workshop.workshop_phone) {
      // Cut list → goes to the workshop
      const digits = workshop.workshop_phone.replace(/[^0-9]/g, "");
      url = `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
    } else if (view !== "cut" && quote?.client_phone) {
      // Price quote → goes to the customer if we have their number
      const digits = quote.client_phone.replace(/[^0-9]/g, "");
      url = `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
    } else {
      url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const copyWorkshopLink = async () => {
    if (!workshopUrl) return;
    try {
      await navigator.clipboard.writeText(workshopUrl);
      toast.success("Workshop link copied");
    } catch {
      // Fallback for older browsers / insecure contexts
      const ta = document.createElement("textarea");
      ta.value = workshopUrl;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        toast.success("Workshop link copied");
      } catch {
        toast.error("Couldn't copy — select & copy manually");
      }
      document.body.removeChild(ta);
    }
  };

  const downloadCutSheetPdf = async () => {
    if (!quote || savingPdf) return;
    setSavingPdf(true);
    try {
      const blob = await getCutSheetPdfBlob(quote.id);
      const safe = (quote.client_name || "cut-sheet").trim().replace(/\s+/g, "_");
      const filename = `cut_sheet_${safe}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoke after a short delay so iOS Safari has time to open the file
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Cut sheet PDF downloaded");
    } catch (e) {
      toast.error("Couldn't generate PDF — try again");
    } finally {
      setSavingPdf(false);
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-5xl print:p-0 print:max-w-none">
      {/* Force PORTRAIT print when the Cut Sheet view is active so workshop
          printouts always come out portrait (rest of app defaults to landscape). */}
      {view === "cut" ? (
        <style>{`@media print { @page { size: A4 portrait; margin: 8mm; } }`}</style>
      ) : null}
      <div className="flex items-center justify-between mb-6 print:hidden flex-wrap gap-3">
        <button
          onClick={() => navigate("/quotes")}
          className="text-xs uppercase tracking-[0.18em] font-semibold text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-2"
          data-testid="back-to-quotes"
        >
          <ArrowLeft size={14} /> All quotes
        </button>

        {/* View toggle */}
        <div
          className="inline-flex border border-[#09090b]"
          data-testid="view-toggle"
        >
          <button
            onClick={() => setView("price")}
            className={`px-4 py-2 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold transition-colors ${
              view === "price"
                ? "bg-[#09090b] text-white"
                : "bg-white text-[#09090b] hover:bg-zinc-100"
            }`}
            data-testid="view-price"
          >
            <Euro size={14} /> Price
          </button>
          <button
            onClick={() => setView("cut")}
            className={`px-4 py-2 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold border-l border-[#09090b] transition-colors ${
              view === "cut"
                ? "bg-[#005bea] text-white"
                : "bg-white text-[#09090b] hover:bg-zinc-100"
            }`}
            data-testid="view-cut"
          >
            <Scissors size={14} /> Cut List
          </button>
        </div>

        <div className="flex gap-2">
          {view === "cut" && workshopUrl ? (
            <button
              onClick={copyWorkshopLink}
              className="btn-sharp secondary"
              data-testid="copy-workshop-link-btn"
              title="Copy a password-free link for the workshop (cut list only, no prices)"
            >
              <LinkIcon size={14} /> Copy link
            </button>
          ) : null}
          <button
            onClick={shareWhatsApp}
            className="btn-sharp"
            style={
              view === "cut"
                ? { background: "#ea580c", borderColor: "#ea580c", color: "white" }
                : { background: "#25D366", borderColor: "#25D366", color: "white" }
            }
            data-testid="whatsapp-share-btn"
            title={
              view === "cut"
                ? workshop.workshop_phone
                  ? `Send cut list to ${workshop.workshop_name || "workshop"}`
                  : "Send cut list (no workshop number set — will open contact picker)"
                : "Send price quote to customer"
            }
          >
            <Share2 size={14} />{" "}
            {view === "cut"
              ? `Send to ${workshop.workshop_name ? workshop.workshop_name : "workshop"}`
              : "Send to customer"}
          </button>
          {view === "cut" ? (
            <button
              onClick={downloadCutSheetPdf}
              disabled={savingPdf}
              className="btn-sharp secondary"
              data-testid="cut-sheet-pdf-btn"
              title="Download cut sheet as PDF (use this if AirPrint isn't working)"
            >
              <Download size={14} /> {savingPdf ? "Building…" : "Save PDF"}
            </button>
          ) : null}
          <button
            onClick={() => window.print()}
            className="btn-sharp secondary"
            data-testid="print-btn"
          >
            <Printer size={14} /> Print
          </button>
        </div>
      </div>

      <div
        className={`border border-[#09090b] ${view === "cut" ? "hidden print:hidden" : ""}`}
      >
        <div className="p-6 md:p-8 border-b border-[#09090b] bg-[#09090b] text-white">
          <div className="flex justify-between items-baseline">
            <div className="label-xs !text-zinc-400">Quote</div>
            {workshop.business_name ? (
              <div
                className="label-xs !text-zinc-400"
                data-testid="quote-business-name"
              >
                {workshop.business_name}
              </div>
            ) : null}
          </div>
          <div className="flex justify-between items-start flex-wrap gap-4 mt-1">
            <div className="flex items-start gap-3 flex-wrap">
              <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
                <RoomNameEdit
                  value={quote.client_name || ""}
                  onCommit={(v) => handleClientNameChange(v)}
                  className="font-display text-4xl sm:text-5xl font-black tracking-tighter w-full"
                  testId="client-name-edit"
                />
              </h1>
              {customerNumberLabel ? (
                <div className="relative">
                  <button
                    type="button"
                    onClick={openMnEditor}
                    title="Tap to change MN number for this quote"
                    // Inline styles force colours: parent header has
                    // `text-white` which can leak into the spans below
                    // through inheritance, blanking out the MN label.
                    style={{ backgroundColor: "#ffffff", color: "#09090b" }}
                    className="font-mono px-3 py-2 inline-flex flex-col items-center hover:bg-zinc-100 transition-colors border border-zinc-200"
                    data-testid="quote-customer-number"
                  >
                    <span
                      style={{ color: "#71717a" }}
                      className="text-[8px] uppercase tracking-[0.18em] inline-flex items-center gap-1"
                    >
                      No.{" "}
                      {Number.isFinite(quote?.customer_number_override) &&
                      quote.customer_number_override > 0 ? (
                        <span
                          style={{ color: "#ea580c" }}
                          title="Custom MN — overrides the customer default"
                        >
                          ✎
                        </span>
                      ) : null}
                    </span>
                    <span
                      style={{ color: "#09090b" }}
                      className="text-base font-bold tabular-nums tracking-[0.12em]"
                    >
                      {customerNumberLabel}
                    </span>
                  </button>
                  {mnEditorOpen ? (
                    <div
                      className="absolute z-30 mt-2 left-0 w-[320px] bg-white border-2 border-[#09090b] shadow-lg p-4"
                      data-testid="mn-editor"
                    >
                      <div className="font-mono text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-700 mb-2">
                        Change MN number for this quote
                      </div>
                      <p className="text-xs text-zinc-600 leading-relaxed mb-3">
                        Pin this single quote to a specific MN-XXX. Other
                        quotes for {quote?.client_name || "this customer"} stay
                        on their current number.
                      </p>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-bold">MN-</span>
                        <input
                          type="number"
                          min="1"
                          max="99999"
                          step="1"
                          inputMode="numeric"
                          value={mnDraft}
                          onChange={(e) => setMnDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleSaveMnOverride(false);
                            if (e.key === "Escape") setMnEditorOpen(false);
                          }}
                          placeholder={mnSuggestion || "007"}
                          autoFocus
                          data-testid="mn-editor-input"
                          className="flex-1 border-2 border-zinc-300 px-3 py-2 font-display text-2xl font-black tabular-nums focus:outline-none focus:border-[#09090b]"
                        />
                      </div>
                      {mnSuggestion ? (
                        <button
                          type="button"
                          onClick={() => setMnDraft(mnSuggestion)}
                          data-testid="mn-editor-suggest"
                          className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-semibold text-[#005bea] hover:text-[#0047c2]"
                        >
                          Use next free · MN-
                          {String(mnSuggestion).padStart(3, "0")}
                        </button>
                      ) : null}
                      <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-zinc-200">
                        {Number.isFinite(quote?.customer_number_override) &&
                        quote.customer_number_override > 0 ? (
                          <button
                            type="button"
                            onClick={() => handleSaveMnOverride(true)}
                            data-testid="mn-editor-reset"
                            className="px-2 py-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-600 hover:text-[#09090b] bg-white border border-zinc-300"
                          >
                            Reset to customer
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => setMnEditorOpen(false)}
                          data-testid="mn-editor-cancel"
                          className="px-2 py-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-600 hover:text-[#09090b] bg-white border border-zinc-300"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => handleSaveMnOverride(false)}
                          data-testid="mn-editor-save"
                          className="px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] font-bold bg-[#09090b] text-white hover:bg-[#005bea]"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div className="text-right">
              <div className="label-xs !text-zinc-400">Total</div>
              <div
                className="font-display text-4xl sm:text-5xl font-black tracking-tighter tabular-nums text-[#005bea]"
                data-testid="detail-total"
              >
                €{(() => {
                  const base = quote.total || 0;
                  const vatRate = parseFloat(workshop?.vat_rate || 0) || 0;
                  // Cash deals are quoted without IVA — headline is the bare
                  // base total, no surcharge inflation.
                  return !quote.cash_payment && vatRate > 0
                    ? (base + Math.round(base * vatRate) / 100).toFixed(2)
                    : base.toFixed(2);
                })()}
              </div>
              {!quote.cash_payment && parseFloat(workshop?.vat_rate || 0) > 0 ? (
                <div className="font-mono text-[10px] text-zinc-400 mt-1">
                  incl. IVA {workshop.vat_rate}%
                </div>
              ) : null}
            </div>
          </div>
          <div className="font-mono text-xs text-zinc-400 mt-4 flex gap-x-6 gap-y-1 flex-wrap">
            <span>
              COLOR ·{" "}
              <button
                type="button"
                onClick={async () => {
                  const v = window.prompt(
                    "Screen colour (e.g. White, Bronze, RAL 7016):",
                    quote.color || ""
                  );
                  if (v === null) return;
                  try {
                    const updated = await updateQuoteMeta(quote.id, { color: v });
                    setQuote(updated);
                    toast.success(v.trim() ? "Colour saved" : "Colour cleared");
                  } catch {
                    toast.error("Failed to save colour");
                  }
                }}
                data-testid="edit-quote-color"
                className="hover:text-white underline-offset-2 hover:underline"
              >
                {quote.color || "add"}
              </button>
            </span>
            <span>
              AREA ·{" "}
              <button
                type="button"
                onClick={async () => {
                  const v = window.prompt(
                    "Area / town (e.g. Mijas, Marbella, La Cala):",
                    quote.area || ""
                  );
                  if (v === null) return;
                  try {
                    const updated = await updateQuoteMeta(quote.id, { area: v });
                    setQuote(updated);
                    toast.success(v.trim() ? "Area saved" : "Area cleared");
                  } catch {
                    toast.error("Failed to save area");
                  }
                }}
                data-testid="edit-quote-area"
                className="hover:text-white underline-offset-2 hover:underline"
              >
                {quote.area || "add"}
              </button>
            </span>
            <span>
              <label className="inline-flex items-center gap-1.5 cursor-pointer hover:text-[#09090b]">
                <input
                  type="checkbox"
                  checked={!!quote.cash_payment}
                  onChange={async (e) => {
                    try {
                      const updated = await updateQuoteMeta(quote.id, {
                        cash_payment: e.target.checked,
                      });
                      setQuote(updated);
                      toast.success(
                        e.target.checked ? "IVA hidden" : "IVA shown"
                      );
                    } catch {
                      toast.error("Couldn't update");
                    }
                  }}
                  className="accent-[#09090b]"
                  data-testid="cash-payment-toggle"
                />
                <span>No IVA</span>
              </label>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span>DISCOUNT ·</span>
              <input
                type="number"
                min="0"
                step={quote.discount_mode === "amount" ? "1" : "0.5"}
                value={
                  quote.discount_mode === "amount"
                    ? quote.discount_amount ?? 0
                    : quote.discount_pct ?? 0
                }
                onChange={(e) => {
                  const v = parseFloat(e.target.value) || 0;
                  if (quote.discount_mode === "amount") {
                    setQuote((q) => ({ ...q, discount_amount: Math.max(0, v) }));
                  } else {
                    setQuote((q) => ({
                      ...q,
                      discount_pct: Math.max(0, Math.min(100, v)),
                    }));
                  }
                }}
                onBlur={async () => {
                  const payload =
                    quote.discount_mode === "amount"
                      ? {
                          discount_mode: "amount",
                          discount_amount: parseFloat(quote.discount_amount) || 0,
                        }
                      : {
                          discount_mode: "pct",
                          discount_pct: parseFloat(quote.discount_pct) || 0,
                        };
                  try {
                    const updated = await updateQuoteMeta(quote.id, payload);
                    setQuote(updated);
                    if ((updated.discount_amount || 0) > 0) {
                      toast.success(`Discount −€${updated.discount_amount.toFixed(2)}`);
                    }
                  } catch {
                    toast.error("Couldn't save discount");
                  }
                }}
                className="w-16 border border-zinc-200 px-1 py-0.5 font-mono text-xs tabular-nums focus:outline-none focus:border-[#09090b] text-right"
                data-testid="discount-value-input"
              />
              <button
                type="button"
                onClick={async () => {
                  const nextMode = quote.discount_mode === "amount" ? "pct" : "amount";
                  try {
                    const updated = await updateQuoteMeta(quote.id, {
                      discount_mode: nextMode,
                    });
                    setQuote(updated);
                  } catch {
                    toast.error("Couldn't switch mode");
                  }
                }}
                className="border border-zinc-300 hover:border-[#09090b] w-7 h-6 inline-flex items-center justify-center font-mono text-xs font-bold focus:outline-none focus:border-[#09090b]"
                title="Toggle % / € mode"
                data-testid="discount-mode-toggle"
              >
                {quote.discount_mode === "amount" ? "€" : "%"}
              </button>
            </span>
            <span>
              PHONE ·{" "}
              <button
                type="button"
                onClick={async () => {
                  const v = window.prompt(
                    "Customer WhatsApp number (with country code):",
                    quote.client_phone || ""
                  );
                  if (v === null) return;
                  try {
                    const updated = await updateQuoteMeta(quote.id, {
                      client_phone: v,
                    });
                    setQuote(updated);
                    toast.success(
                      v.trim()
                        ? "Phone saved"
                        : "Phone cleared"
                    );
                  } catch {
                    toast.error("Failed to save phone");
                  }
                }}
                data-testid="edit-client-phone"
                className="hover:text-white underline-offset-2 hover:underline tabular-nums"
              >
                {quote.client_phone || "add"}
              </button>
            </span>
            <span>
              ADDRESS ·{" "}
              <button
                type="button"
                onClick={async () => {
                  const v = window.prompt(
                    "Customer address:",
                    quote.client_address || ""
                  );
                  if (v === null) return;
                  try {
                    const updated = await updateQuoteMeta(quote.id, {
                      client_address: v,
                    });
                    setQuote(updated);
                    toast.success(
                      v.trim() ? "Address saved" : "Address cleared"
                    );
                  } catch {
                    toast.error("Failed to save address");
                  }
                }}
                data-testid="edit-client-address"
                className="hover:text-white underline-offset-2 hover:underline"
              >
                {quote.client_address || "add"}
              </button>
            </span>
            <span>DATE · {new Date(quote.created_at).toLocaleDateString()}</span>
          </div>

          {/* Status badges */}
          {(quote.confirmed_at ||
            quote.produced_at ||
            quote.deposit_paid_at ||
            quote.final_paid_at) && (
            <div className="mt-5 flex flex-wrap gap-2" data-testid="status-badges">
              {quote.confirmed_at && (
                <div
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#005bea] text-white"
                  data-testid="status-confirmed"
                >
                  <CheckCircle2 size={14} />
                  <span className="font-mono text-[11px] uppercase tracking-[0.18em] font-semibold">
                    Order confirmed · {fmtDate(quote.confirmed_at)}
                  </span>
                </div>
              )}
              {quote.scheduled_cut_date && (
                <div
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#7c3aed] text-white"
                  data-testid="status-scheduled"
                >
                  <Calendar size={14} />
                  <span className="font-mono text-[11px] uppercase tracking-[0.18em] font-semibold">
                    Cut day · {quote.scheduled_cut_date}
                  </span>
                </div>
              )}
              {quote.invoice_number && (
                <div
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#0f766e] text-white"
                  data-testid="status-invoice"
                >
                  <FileText size={14} />
                  <span className="font-mono text-[11px] uppercase tracking-[0.18em] font-semibold">
                    {quote.invoice_number}
                  </span>
                </div>
              )}
              {quote.produced_at && (
                <div
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#16a34a] text-white"
                  data-testid="status-produced"
                >
                  <PackageCheck size={14} />
                  <span className="font-mono text-[11px] uppercase tracking-[0.18em] font-semibold">
                    Sizes done · {fmtDate(quote.produced_at)}
                  </span>
                </div>
              )}
              {quote.deposit_paid_at && (
                <div
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#eab308] text-[#09090b]"
                  data-testid="status-deposit"
                >
                  <Wallet size={14} />
                  <span className="font-mono text-[11px] uppercase tracking-[0.18em] font-semibold">
                    Deposit paid
                    {quote.deposit_amount != null
                      ? ` · €${quote.deposit_amount.toFixed(2)}`
                      : ""}
                    {" · "}
                    {fmtDate(quote.deposit_paid_at)}
                  </span>
                </div>
              )}
              {quote.final_paid_at && (
                <div
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#09090b] text-white border border-[#16a34a]"
                  data-testid="status-final"
                >
                  <BadgeEuro size={14} className="text-[#16a34a]" />
                  <span className="font-mono text-[11px] uppercase tracking-[0.18em] font-semibold">
                    Paid in full · {fmtDate(quote.final_paid_at)}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Action buttons (hidden when printing) */}
          <div className="mt-5 print:hidden flex flex-wrap gap-2 items-center">
            {!quote.confirmed_at ? (
              <button
                onClick={handleConfirm}
                disabled={confirming}
                className="btn-sharp primary"
                data-testid="confirm-order-btn"
              >
                <CheckCircle2 size={14} />{" "}
                {confirming ? "Confirming..." : "Confirm order & open cut list"}
              </button>
            ) : (
              <button
                onClick={handleUnconfirm}
                className="text-[11px] uppercase tracking-[0.18em] font-semibold text-zinc-400 hover:text-white inline-flex items-center gap-1"
                data-testid="unconfirm-btn"
              >
                Undo confirm
              </button>
            )}

            {!quote.deposit_paid_at ? (
              <button
                onClick={handleDeposit}
                className="btn-sharp secondary"
                data-testid="mark-deposit-btn"
              >
                <Wallet size={14} /> Mark deposit paid
              </button>
            ) : (
              <button
                onClick={() => handleClearPayment("deposit")}
                className="text-[11px] uppercase tracking-[0.18em] font-semibold text-zinc-400 hover:text-white inline-flex items-center gap-1"
                data-testid="clear-deposit-btn"
              >
                Undo deposit
              </button>
            )}

            {quote.confirmed_at && (
              <button
                onClick={handleSchedule}
                className="btn-sharp secondary"
                data-testid="schedule-cut-btn"
              >
                <Calendar size={14} />{" "}
                {quote.scheduled_cut_date
                  ? `Cut day: ${quote.scheduled_cut_date}`
                  : "Schedule cut day"}
              </button>
            )}

            {!quote.cash_payment && (
              <button
                onClick={handleInvoice}
                className="btn-sharp secondary"
                data-testid="invoice-pdf-btn"
                title={
                  quote.invoice_number
                    ? `Re-download ${quote.invoice_number}`
                    : "Issue invoice number and download PDF"
                }
              >
                <FileText size={14} />{" "}
                {quote.invoice_number
                  ? `${quote.invoice_number} · PDF`
                  : "Issue invoice (PDF)"}
              </button>
            )}

            {!quote.final_paid_at ? (
              <button
                onClick={handleFullPayment}
                className="btn-sharp secondary"
                data-testid="mark-final-btn"
              >
                <BadgeEuro size={14} /> Mark paid in full
              </button>
            ) : (
              <button
                onClick={() => handleClearPayment("final")}
                className="text-[11px] uppercase tracking-[0.18em] font-semibold text-zinc-400 hover:text-white inline-flex items-center gap-1"
                data-testid="clear-final-btn"
              >
                Undo full payment
              </button>
            )}
          </div>
        </div>

        <div className="p-4 border-b border-zinc-200 bg-[#f4f4f5] print:hidden">
            <div className="flex items-center justify-between gap-3 mb-2">
              <div className="label-xs">
                {quote.photos && quote.photos.length > 0
                  ? `Source ${quote.photos.length > 1 ? `Sketches · ${quote.photos.length} pages` : "Sketch"}`
                  : "Source Sketch / Photo"}
              </div>
              <div className="flex items-center gap-2">
                <label
                  htmlFor={`photo-camera-${id}`}
                  className={`btn-sharp secondary cursor-pointer ${uploadingPhoto ? "opacity-60 pointer-events-none" : ""}`}
                  data-testid="attach-photo-camera-btn"
                >
                  <Camera size={14} /> Take photo
                </label>
                <input
                  id={`photo-camera-${id}`}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={handlePhotoUpload}
                  data-testid="attach-photo-camera-input"
                />
                <label
                  htmlFor={`photo-gallery-${id}`}
                  className={`btn-sharp secondary cursor-pointer ${uploadingPhoto ? "opacity-60 pointer-events-none" : ""}`}
                  data-testid="attach-photo-gallery-btn"
                >
                  <ImagePlus size={14} />{" "}
                  {quote.photos && quote.photos.length > 0 ? "Add page" : "Choose photo"}
                </label>
                <input
                  id={`photo-gallery-${id}`}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handlePhotoUpload}
                  data-testid="attach-photo-gallery-input"
                />
              </div>
            </div>
            {quote.photos && quote.photos.length > 0 ? (
              <div className="flex flex-wrap gap-3">
                {quote.photos.map((src, i) => (
                  <div key={i} className="relative group">
                    <img
                      src={src}
                      alt={`sketch-${i}`}
                      loading="lazy"
                      decoding="async"
                      className="max-w-full sm:max-w-[300px] max-h-[300px] object-contain border border-zinc-200 bg-white"
                      data-testid={`source-photo-${i}`}
                    />
                    <button
                      type="button"
                      onClick={() => handleRemovePhoto(i)}
                      className="absolute top-1 right-1 bg-white border border-zinc-300 hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors p-1"
                      title="Remove photo"
                      data-testid={`remove-photo-${i}`}
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
                {uploadingPhoto && (
                  <div className="flex items-center justify-center w-[120px] h-[120px] border border-dashed border-zinc-300 text-[11px] uppercase tracking-[0.18em] text-zinc-500">
                    Uploading…
                  </div>
                )}
              </div>
            ) : (
              <div className="text-xs text-zinc-500">
                {uploadingPhoto
                  ? "Uploading…"
                  : "No photo on file. Tap 'Take photo' to snap one with the camera, or 'Choose photo' to pick from your gallery."}
              </div>
            )}
          </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm font-mono">
            <thead className="bg-[#f4f4f5]">
              <tr className="text-left">
                <th className="px-4 py-3 label-xs">Room</th>
                <th className="px-4 py-3 label-xs">Opening</th>
                <th className="px-4 py-3 label-xs">Leaf</th>
                <th className="px-4 py-3 label-xs">m²</th>
                <th className="px-4 py-3 label-xs">Type</th>
                <th className="px-4 py-3 label-xs">Colour</th>
                <th className="px-4 py-3 label-xs text-right">Price</th>
              </tr>
            </thead>
            <tbody>
              {quote.rooms.map((r) => (
                <tr key={r.id} className="border-t border-zinc-200">
                  <td className="px-4 py-3 font-display text-xl font-black tracking-tight">
                    <div className="flex items-center gap-2">
                      <RoomNameEdit
                        value={r.name}
                        onCommit={(v) => handleRoomNameChange(r.id, v)}
                        testId={`room-name-${r.id}`}
                      />
                      {r.excluded && (
                        <span
                          className="px-1.5 py-0.5 bg-zinc-200 text-zinc-700 text-[9px] uppercase tracking-[0.18em] font-semibold"
                          title="This room is included in the customer's price but excluded from the workshop cut sheet."
                        >
                          Not cutting
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => handleToggleRoomExcluded(r.id, !!r.excluded)}
                        className={`print:hidden p-1 border transition-colors ${
                          r.excluded
                            ? "border-[#005bea] text-[#005bea] hover:bg-[#005bea] hover:text-white"
                            : "border-transparent text-zinc-400 hover:border-[#ff5722] hover:text-[#ff5722]"
                        }`}
                        title={
                          r.excluded
                            ? "Restore — show this room on the workshop cut sheet again"
                            : "Hide from workshop cut sheet (kept in customer quote)"
                        }
                        data-testid={`price-toggle-room-excluded-${r.id}`}
                        aria-label={r.excluded ? "Restore room" : "Hide from cut sheet"}
                      >
                        {r.excluded ? <Eye size={14} /> : <EyeOff size={14} />}
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-zinc-600 tabular-nums">
                    <div className="flex items-end gap-1">
                      <div className="flex flex-col items-center">
                        <span className="text-[9px] uppercase tracking-[0.18em] font-bold text-zinc-400 leading-none mb-0.5">
                          W
                        </span>
                        <LeafInput
                          value={r.width_mm}
                          onCommit={(v) => handleRoomDimChange(r.id, "width_mm", v)}
                          testId={`width-input-${r.id}`}
                        />
                      </div>
                      <span className="text-zinc-400 pb-1.5">×</span>
                      <div className="flex flex-col items-center">
                        <span className="text-[9px] uppercase tracking-[0.18em] font-bold text-zinc-400 leading-none mb-0.5">
                          H
                        </span>
                        <LeafInput
                          value={r.height_mm}
                          onCommit={(v) => handleRoomDimChange(r.id, "height_mm", v)}
                          testId={`height-input-${r.id}`}
                        />
                      </div>
                      <span className="text-[11px] text-zinc-400 ml-1 pb-1.5">mm</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <LeafInput
                      value={r.leaf_width_mm || ""}
                      onCommit={(v) => handleRoomLeafChange(r.id, v)}
                      testId={`leaf-input-${r.id}`}
                    />
                  </td>
                  <td className="px-4 py-3 tabular-nums">
                    {r.area_sqm.toFixed(3)}
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={r.screen_type || ""}
                      onChange={(e) => handleRoomTypeChange(r.id, e.target.value)}
                      data-testid={`room-type-select-${r.id}`}
                      className={`w-full border px-2 py-1 font-mono text-xs bg-white rounded-none focus:outline-none focus:border-[#09090b] ${
                        r.screen_type ? "border-zinc-300" : "border-[#ff5722] text-[#ff5722]"
                      }`}
                    >
                      <option value="">— Choose type —</option>
                      {types.map((t) => (
                        <option key={t.key} value={t.key}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                    {r.matched_tier_mm != null && (
                      <div
                        className={`text-[10px] mt-1 ${
                          r.oversized ? "text-[#ff5722] font-bold" : "text-zinc-500"
                        }`}
                      >
                        {r.oversized
                          ? `OVER · ${r.matched_tier_mm}mm tier`
                          : `${r.matched_tier_mm}mm tier`}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={r.color_key || ""}
                      onChange={(e) => handleRoomColorChange(r.id, e.target.value)}
                      data-testid={`room-color-select-${r.id}`}
                      className="w-full border border-zinc-300 px-2 py-1 font-mono text-xs bg-white rounded-none focus:outline-none focus:border-[#09090b]"
                    >
                      <option value="">— White (default) —</option>
                      {colors.map((c) => (
                        <option key={c.key} value={c.key}>
                          {c.label}
                          {c.multiplier_pct ? ` +${c.multiplier_pct}%` : ""}
                        </option>
                      ))}
                    </select>
                    {r.color_key && r.color_key !== "white" ? (
                      <input
                        type="text"
                        defaultValue={r.color_detail || ""}
                        onBlur={(e) => handleRoomColorDetailChange(r.id, e.target.value)}
                        placeholder={
                          (r.color_key || "").includes("ral")
                            ? "e.g. RAL 7016"
                            : (r.color_key || "").includes("wood")
                              ? "e.g. Light Oak"
                              : "Specific finish"
                        }
                        data-testid={`room-color-detail-${r.id}`}
                        className="w-full border border-zinc-300 px-2 py-1 mt-1 text-xs bg-white rounded-none focus:outline-none focus:border-[#09090b]"
                      />
                    ) : null}
                    {r.color_upcharge ? (
                      <div className="text-[10px] mt-1 text-zinc-500">
                        +€{r.color_upcharge.toFixed(2)}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold">
                    €{(r.price + (r.color_upcharge || 0)).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-[#f4f4f5]">
              {(() => {
                const base = quote.base_subtotal || 0;
                const surcharge = quote.color_upcharge || 0;
                const discountAmount = quote.discount_amount || 0;
                // Pre-IVA subtotal: base + surcharge − discount for new quotes;
                // for legacy quotes that had no breakdown, fall back to total.
                const preTax =
                  base || surcharge
                    ? base + surcharge - discountAmount
                    : quote.total || 0;
                // Cash deals: no IVA shown anywhere on screen.
                const vatRate = quote.cash_payment
                  ? 0
                  : parseFloat(workshop?.vat_rate || 0) || 0;
                const ivaAmount = vatRate > 0
                  ? Math.round(preTax * vatRate) / 100
                  : 0;
                const grandTotal = preTax + ivaAmount;
                const showSurchargeRow = surcharge > 0;
                const showDiscountRow = discountAmount > 0;
                const showIvaRow = vatRate > 0;
                return (
                  <>
                    {showSurchargeRow && (
                      <>
                        <tr className="border-t border-zinc-300">
                          <td colSpan="6" className="px-4 py-2 text-right text-xs text-zinc-600">
                            Base subtotal
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums">
                            €{base.toFixed(2)}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan="6" className="px-4 py-2 text-right text-xs text-zinc-600">
                            Colour surcharge
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums">
                            +€{surcharge.toFixed(2)}
                          </td>
                        </tr>
                      </>
                    )}
                    {showDiscountRow && (
                      <tr className={showSurchargeRow ? "" : "border-t border-zinc-300"}>
                        <td colSpan="6" className="px-4 py-2 text-right text-xs text-zinc-600">
                          Discount{
                            quote.discount_mode === "amount"
                              ? ""
                              : quote.discount_pct
                                ? ` (${quote.discount_pct}%)`
                                : ""
                          }
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums text-[#005bea]">
                          −€{discountAmount.toFixed(2)}
                        </td>
                      </tr>
                    )}
                    {showIvaRow && (
                      <>
                        <tr className={showSurchargeRow || showDiscountRow ? "" : "border-t border-zinc-300"}>
                          <td colSpan="6" className="px-4 py-2 text-right text-xs text-zinc-600">
                            Subtotal
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums">
                            €{preTax.toFixed(2)}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan="6" className="px-4 py-2 text-right text-xs text-zinc-600">
                            IVA ({vatRate}%)
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums">
                            +€{ivaAmount.toFixed(2)}
                          </td>
                        </tr>
                      </>
                    )}
                    <tr className="border-t-2 border-[#09090b]">
                      <td colSpan="6" className="px-4 py-4 label-xs text-right">
                        Total {showIvaRow ? "(incl. IVA)" : ""}
                      </td>
                      <td className="px-4 py-4 text-right font-display text-2xl font-black tabular-nums">
                        €{grandTotal.toFixed(2)}
                      </td>
                    </tr>
                  </>
                );
              })()}
            </tfoot>
          </table>
        </div>
      </div>

      {/* Production Cut List */}
      {view === "cut" && (
        <div
          className="mt-2 border border-[#09090b]"
          data-testid="production-cut-list"
        >
          <div className="p-5 md:p-6 border-b border-[#09090b] bg-[#005bea] text-white">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <div className="label-xs !text-blue-100">Production</div>
                <div className="flex items-center gap-3 flex-wrap mt-1">
                  <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tighter">
                    CUT LIST — {quote.client_name || "(Unnamed)"}
                  </h2>
                  {customerNumberLabel ? (
                    <div
                      className="bg-white text-[#005bea] font-mono px-2 py-1 inline-flex flex-col items-center"
                      data-testid="cut-list-customer-number"
                    >
                      <span className="text-[#005bea]/70 text-[8px] uppercase tracking-[0.18em]">
                        No.
                      </span>
                      <span className="text-sm font-bold tabular-nums tracking-[0.12em]">
                        {customerNumberLabel}
                      </span>
                    </div>
                  ) : null}
                </div>
                <div className="font-mono text-xs text-blue-100 mt-2">
                  {quote.rooms.length} item(s) · {quote.color || "color TBD"} ·{" "}
                  {quote.area || "area TBD"}
                  {quote.rooms.filter((r) => r.excluded).length > 0 && (
                    <span className="ml-2 px-1.5 py-0.5 bg-blue-900/40 border border-blue-200/30 text-[10px] uppercase tracking-[0.18em] font-semibold">
                      {quote.rooms.filter((r) => r.excluded).length} hidden — restore from Price view
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right print:hidden">
                <div className="label-xs !text-blue-100">Progress</div>
                <div
                  className="font-display text-3xl font-black tabular-nums"
                  data-testid="cut-progress"
                >
                  {doneCount} / {totalPieces}
                </div>
                {doneCount > 0 && (
                  <button
                    onClick={resetAll}
                    className="mt-2 text-[10px] uppercase tracking-[0.18em] font-semibold text-blue-100 hover:text-white inline-flex items-center gap-1"
                    data-testid="reset-cuts-btn"
                  >
                    <RotateCcw size={11} /> Reset
                  </button>
                )}
              </div>
            </div>
          </div>
          {/* Big, obvious schedule button on the Cut Sheet — once scheduled,
              this room appears on the Day Plan for that date so the workshop
              guy knows exactly what's getting cut. */}
          <div className="print:hidden border-b border-zinc-200 bg-[#f4f4f5] px-5 py-4">
            <button
              type="button"
              onClick={handleSchedule}
              className={`w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 border-2 font-display text-sm font-black tracking-tight uppercase transition-colors ${
                quote.scheduled_cut_date
                  ? "border-[#7c3aed] bg-white text-[#7c3aed] hover:bg-[#7c3aed] hover:text-white"
                  : "border-[#005bea] bg-[#005bea] text-white hover:bg-[#0047c2]"
              }`}
              data-testid="cut-sheet-schedule-btn"
            >
              <Calendar size={16} />
              {quote.scheduled_cut_date
                ? `Cutting ${quote.scheduled_cut_date} · change date`
                : "Schedule for cut day"}
            </button>
            {quote.scheduled_cut_date && (
              <span className="ml-3 text-xs text-zinc-600 font-mono">
                On the workshop's Day Plan for{" "}
                <span className="font-bold">{quote.scheduled_cut_date}</span>.
              </span>
            )}
          </div>
          <div className="divide-y divide-zinc-200">
            {quote.rooms.filter((r) => !r.excluded).map((r) => {
              const idx = quote.rooms.indexOf(r);
              return (
              <div
                key={r.id}
                className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4 p-5 print:break-inside-avoid"
                data-testid={`cut-block-${idx}`}
              >
                <div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="label-xs">
                      Item {String(idx + 1).padStart(2, "0")}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleToggleRoomExcluded(r.id, !!r.excluded)}
                      className="print:hidden p-2 -m-1 border border-transparent text-zinc-400 hover:border-[#ff5722] hover:text-[#ff5722] transition-colors"
                      title="Hide from cut sheet (kept in quote total + invoice)"
                      data-testid={`toggle-room-excluded-${r.id}`}
                      aria-label="Hide from cut sheet"
                    >
                      <EyeOff size={16} />
                    </button>
                  </div>
                  <div className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-1 leading-none">
                    <RoomNameEdit
                      value={r.name}
                      onCommit={(v) => handleRoomNameChange(r.id, v)}
                      testId={`cut-room-name-${r.id}`}
                      className="font-display text-3xl sm:text-4xl font-black tracking-tighter leading-none"
                    />
                  </div>
                  <select
                    value={r.screen_type || ""}
                    onChange={(e) => handleRoomTypeChange(r.id, e.target.value)}
                    data-testid={`cut-room-type-select-${r.id}`}
                    className={`mt-2 w-full border px-2 py-1 font-mono text-xs bg-white rounded-none print:hidden focus:outline-none focus:border-[#09090b] ${
                      r.screen_type ? "border-zinc-300" : "border-[#ff5722] text-[#ff5722]"
                    }`}
                  >
                    <option value="">— Choose type —</option>
                    {types.map((t) => (
                      <option key={t.key} value={t.key}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                  <div className="hidden print:block font-mono text-xs text-zinc-600 mt-2">
                    {typeLabel(r.screen_type)}
                  </div>
                  <div className="font-mono text-[11px] text-zinc-500 mt-1 print:block hidden">
                    opening {r.width_mm} × {r.height_mm} mm
                  </div>
                  <div className="print:hidden mt-2 flex flex-wrap items-end gap-2">
                    <label className="label-xs pb-1.5">Opening</label>
                    <div className="flex flex-col items-center">
                      <span className="text-[9px] uppercase tracking-[0.18em] font-bold text-zinc-400 leading-none mb-0.5">
                        W
                      </span>
                      <LeafInput
                        value={r.width_mm}
                        onCommit={(v) => handleRoomDimChange(r.id, "width_mm", v)}
                        testId={`cut-width-input-${r.id}`}
                      />
                    </div>
                    <span className="text-zinc-400 pb-1.5">×</span>
                    <div className="flex flex-col items-center">
                      <span className="text-[9px] uppercase tracking-[0.18em] font-bold text-zinc-400 leading-none mb-0.5">
                        H
                      </span>
                      <LeafInput
                        value={r.height_mm}
                        onCommit={(v) => handleRoomDimChange(r.id, "height_mm", v)}
                        testId={`cut-height-input-${r.id}`}
                      />
                    </div>
                    <span className="font-mono text-[10px] text-zinc-400 pb-1.5">mm</span>
                  </div>
                  <div className="print:hidden mt-2 flex items-center gap-x-4 gap-y-2 flex-wrap">
                    <span className="inline-flex items-center gap-1 whitespace-nowrap">
                      <label className="label-xs">Leaf</label>
                      <LeafInput
                        value={r.leaf_width_mm || ""}
                        onCommit={(v) => handleRoomLeafChange(r.id, v)}
                        testId={`cut-leaf-input-${r.id}`}
                      />
                      <span className="font-mono text-[10px] text-zinc-400">mm</span>
                    </span>
                    <span className="inline-flex items-center gap-1 whitespace-nowrap">
                      <label className="label-xs">Track</label>
                      <LeafInput
                        value={r.track_width_mm || ""}
                        onCommit={(v) => handleRoomTrackChange(r.id, v)}
                        testId={`cut-track-input-${r.id}`}
                      />
                      <span className="font-mono text-[10px] text-zinc-400">mm</span>
                    </span>
                  </div>
                  {r.leaf_width_mm || r.track_width_mm ? (
                    <div className="hidden print:block font-mono text-[11px] text-zinc-500 mt-1">
                      {r.leaf_width_mm ? `leaf ${r.leaf_width_mm} mm` : ""}
                      {r.leaf_width_mm && r.track_width_mm ? " · " : ""}
                      {r.track_width_mm ? `track ${r.track_width_mm} mm` : ""}
                    </div>
                  ) : null}
                </div>
                <div>
                  {r.cut_pieces && r.cut_pieces.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {r.cut_pieces.map((p, pi) => {
                        const key = `${r.id}:${pi}`;
                        const done = checks.has(key);
                        const pieceKey = `${r.id}:${p.label}`;
                        const isEditing = editingPieceKey === pieceKey;
                        const overrideValue =
                          (r.piece_overrides || {})[p.label];
                        const isOverridden =
                          overrideValue !== undefined &&
                          overrideValue !== null &&
                          overrideValue > 0;
                        const shape = (r.piece_shapes || {})[p.label] || "";

                        // Long-press: starts a 500ms timer on pointerdown.
                        // If the user lifts their finger within that window
                        // (short tap) we fall through to toggleCheck.
                        const startLongPress = (e) => {
                          // Don't trigger long-press on right-click / multi-touch
                          if (e.button && e.button !== 0) return;
                          if (longPressTimerRef.current)
                            clearTimeout(longPressTimerRef.current);
                          longPressTimerRef.current = setTimeout(() => {
                            setEditingPieceKey(pieceKey);
                            setEditingPieceDraft(
                              isOverridden
                                ? String(overrideValue)
                                : ""
                            );
                            longPressTimerRef.current = null;
                          }, 500);
                        };
                        const cancelLongPress = () => {
                          if (longPressTimerRef.current) {
                            clearTimeout(longPressTimerRef.current);
                            longPressTimerRef.current = null;
                          }
                        };
                        const handleTap = (e) => {
                          // If the long-press fired, it has already cleared
                          // the ref and opened the editor — don't also toggle.
                          if (longPressTimerRef.current === null && isEditing)
                            return;
                          cancelLongPress();
                          toggleCheck(key);
                        };

                        const commitOverride = () => {
                          handlePieceOverride(
                            r.id,
                            p.label,
                            editingPieceDraft
                          );
                          setEditingPieceKey("");
                        };
                        const clearOverride = () => {
                          handlePieceOverride(r.id, p.label, null);
                          setEditingPieceKey("");
                        };

                        if (isEditing) {
                          return (
                            <div
                              key={pi}
                              data-testid={`piece-override-editor-${idx}-${pi}`}
                              className="border-2 border-orange-500 bg-orange-50 px-3 py-2 flex items-center gap-2"
                            >
                              <div className="flex-1 min-w-0">
                                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                                  <Pencil size={10} /> {p.label}
                                </div>
                                <div className="flex items-center gap-2 mt-1">
                                  <input
                                    type="number"
                                    inputMode="numeric"
                                    min="0"
                                    step="1"
                                    autoFocus
                                    value={editingPieceDraft}
                                    onChange={(e) =>
                                      setEditingPieceDraft(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") commitOverride();
                                      if (e.key === "Escape")
                                        setEditingPieceKey("");
                                    }}
                                    placeholder={`${p.length_mm} (formula)`}
                                    data-testid={`piece-override-input-${idx}-${pi}`}
                                    className="w-24 font-display text-xl font-black tabular-nums border-2 border-orange-500 bg-white px-2 py-1 focus:outline-none"
                                  />
                                  <span className="font-mono text-xs text-zinc-500">
                                    mm
                                  </span>
                                </div>
                                {/* U / L shape chooser — only stored when set */}
                                <div className="flex items-center gap-1 mt-2">
                                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 mr-1">
                                    Shape:
                                  </span>
                                  {["", "U", "L"].map((s) => {
                                    const active = shape === s;
                                    const labelText = s === "" ? "—" : s;
                                    return (
                                      <button
                                        key={s || "none"}
                                        type="button"
                                        onPointerDown={(e) =>
                                          e.preventDefault()
                                        }
                                        onClick={() =>
                                          handlePieceShape(r.id, p.label, s)
                                        }
                                        data-testid={`piece-shape-${idx}-${pi}-${s || "none"}`}
                                        className={`w-8 h-8 font-display font-black text-base flex items-center justify-center border-2 transition-colors ${
                                          active
                                            ? "bg-orange-500 border-orange-500 text-white"
                                            : "bg-white border-zinc-300 text-zinc-700 hover:border-orange-400"
                                        }`}
                                      >
                                        {labelText}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                              <div className="flex flex-col gap-1">
                                <button
                                  type="button"
                                  onPointerDown={(e) => e.preventDefault()}
                                  onClick={commitOverride}
                                  data-testid={`piece-override-save-${idx}-${pi}`}
                                  className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] font-bold bg-orange-500 text-white hover:bg-orange-600"
                                >
                                  Save
                                </button>
                                {isOverridden ? (
                                  <button
                                    type="button"
                                    onPointerDown={(e) => e.preventDefault()}
                                    onClick={clearOverride}
                                    data-testid={`piece-override-reset-${idx}-${pi}`}
                                    className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-600 hover:text-[#09090b] bg-white border border-zinc-300"
                                    title="Reset to formula"
                                  >
                                    Reset
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onPointerDown={(e) => e.preventDefault()}
                                    onClick={() => setEditingPieceKey("")}
                                    className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-600 hover:text-[#09090b] bg-white border border-zinc-300"
                                  >
                                    Done
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        }

                        return (
                          <button
                            key={pi}
                            type="button"
                            onPointerDown={startLongPress}
                            onPointerUp={handleTap}
                            onPointerLeave={cancelLongPress}
                            onPointerCancel={cancelLongPress}
                            onContextMenu={(e) => {
                              // Right-click / two-finger tap on trackpad
                              // opens the override editor too.
                              e.preventDefault();
                              cancelLongPress();
                              setEditingPieceKey(pieceKey);
                              setEditingPieceDraft(
                                isOverridden ? String(overrideValue) : ""
                              );
                            }}
                            data-testid={`cut-check-${idx}-${pi}`}
                            title="Tap to tick · Hold to edit length"
                            className={`relative border px-3 py-2 flex items-center justify-between gap-3 text-left transition-colors select-none ${
                              isOverridden
                                ? done
                                  ? "border-orange-400 bg-orange-50/70"
                                  : "border-orange-400 bg-orange-50"
                                : done
                                ? "border-[#005bea] bg-blue-50"
                                : "border-zinc-200 bg-white hover:bg-zinc-50"
                            }`}
                          >
                            <div
                              className={`w-6 h-6 border-2 flex items-center justify-center shrink-0 transition-colors ${
                                done
                                  ? "border-[#005bea] bg-[#005bea]"
                                  : "border-zinc-300 bg-white"
                              }`}
                            >
                              {done && (
                                <Check
                                  size={14}
                                  strokeWidth={3}
                                  className="text-white"
                                />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div
                                className={`font-mono text-[10px] uppercase tracking-widest flex items-center gap-1 ${
                                  done ? "text-zinc-400 line-through" : "text-zinc-500"
                                }`}
                              >
                                {isOverridden ? (
                                  <Pencil
                                    size={10}
                                    className="text-orange-600 print:hidden"
                                    data-testid={`piece-override-badge-${idx}-${pi}`}
                                  />
                                ) : null}
                                {p.label}
                              </div>
                              <div
                                className={`font-display text-xl font-black tabular-nums flex items-center gap-2 ${
                                  done ? "text-zinc-400 line-through" : ""
                                }`}
                              >
                                <span>
                                  <span
                                    className={
                                      done ? "text-zinc-400" : "text-[#005bea]"
                                    }
                                  >
                                    {p.qty}/
                                  </span>{" "}
                                  <span
                                    className={
                                      isOverridden && !done
                                        ? "text-orange-600"
                                        : ""
                                    }
                                  >
                                    {p.length_mm}
                                  </span>
                                </span>
                                {shape ? (
                                  <span
                                    data-testid={`piece-shape-chip-${idx}-${pi}`}
                                    className="inline-flex items-center justify-center w-7 h-7 bg-orange-500 text-white font-display font-black text-sm leading-none"
                                    title={`${shape}-shape track`}
                                  >
                                    {shape}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div
                      className="font-mono text-xs italic"
                      data-testid={`cut-empty-${idx}`}
                    >
                      {r.screen_type ? (
                        <span className="text-zinc-400">
                          No cut pieces configured for this screen type. Edit it in{" "}
                          <button
                            onClick={() => navigate("/settings")}
                            className="underline text-[#005bea] hover:text-[#09090b]"
                          >
                            Pricing
                          </button>
                          .
                        </span>
                      ) : (
                        <span className="text-[#ff5722]">
                          Pick a screen type above to generate the cut list.
                        </span>
                      )}
                    </div>
                  )}

                  {/* Per-room sketch — drawable area for hinge/swing/extra notes */}
                  <div className="mt-4 print:mt-3" data-testid={`sketch-block-${idx}`}>
                    {r.sketch_data_url ? (
                      <div className="border border-zinc-200">
                        <div className="flex items-center justify-between px-3 py-2 bg-zinc-50 border-b border-zinc-200 print:hidden">
                          <span className="font-mono text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-600 inline-flex items-center gap-1">
                            <Pencil size={11} /> Sketch
                          </span>
                          <button
                            type="button"
                            onClick={() => setSketchOpenRoomId(r.id)}
                            data-testid={`sketch-edit-${idx}`}
                            className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#005bea] hover:text-[#09090b]"
                          >
                            Edit sketch
                          </button>
                        </div>
                        <img
                          src={r.sketch_data_url}
                          alt={`Sketch for ${r.name}`}
                          className="w-full block bg-white"
                          data-testid={`sketch-img-${idx}`}
                        />
                      </div>
                    ) : sketchOpenRoomId === r.id ? null : (
                      <button
                        type="button"
                        onClick={() => setSketchOpenRoomId(r.id)}
                        data-testid={`sketch-add-${idx}`}
                        className="print:hidden inline-flex items-center gap-2 px-3 py-2 border border-dashed border-zinc-300 text-zinc-600 hover:border-[#09090b] hover:text-[#09090b] font-mono text-[11px] uppercase tracking-[0.18em] font-semibold"
                      >
                        <Pencil size={12} /> Add sketch / notes
                      </button>
                    )}
                    {sketchOpenRoomId === r.id ? (
                      <div className="mt-2 print:hidden">
                        <SketchPad
                          initialDataUrl={r.sketch_data_url || ""}
                          onSave={(d) => handleSaveSketch(r.id, d)}
                          onCancel={() => setSketchOpenRoomId("")}
                          onClear={() => handleSaveSketch(r.id, "")}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteDetail;
